import { ArrowOutwardSharp, ErrorOutline } from '@mui/icons-material';
import React from 'react';

const formatCellContent = (content) => {
  if (content === null || content === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }
  if (typeof content === 'object') {
    return JSON.stringify(content);
  }
  return String(content);
};

export default function DiffCard({ diff, headers, uniqueField, isSelected, onSelect, onOpenModal, errorMessage }) {
  const { from_db_row, to_db_row, fields_different } = diff;
  const uniqueId = (from_db_row || to_db_row)[uniqueField];
  
  const isModified = from_db_row && to_db_row;  
  const isFailed = !!errorMessage;

  const scrollbarClasses = "scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500";
  const cardClasses = `bg-white border rounded-xl shadow-sm mb-6 flex flex-col overflow-hidden ${
    isFailed ? 'border-red-500 border-2' : 'border-gray-200'
  }`;

  return (
    <div className={cardClasses}>
      {isFailed && (
        <div className="p-3 bg-red-50 text-red-800 border-b-2 border-red-200 flex items-center gap-3">
          <ErrorOutline fontSize="small" />
          <p className="text-sm">
            <span className="font-semibold">Migration Error:</span> {errorMessage}
          </p>
        </div>
      )}

      <div className="flex">
      {/* Checkbox for selection */}
      <div className="p-4 flex items-center justify-center bg-gray-50/50 border-r border-gray-200">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(uniqueId)}
          className="h-5 w-5 rounded border-gray-300 text-black focus:ring-black"
        />
      </div>

      {/* Grid for headers and data rows */}
      <div className={`flex-grow overflow-x-auto ${scrollbarClasses}`}>
        <div 
          className="grid" 
          style={{ gridTemplateColumns: `minmax(100px, auto) repeat(${headers.length}, minmax(200px, 1fr))` }}
        >
          {/* Headers Row */}
            <div className="p-2 text-xs font-semibold text-gray-500 bg-gray-50/50 border-b border-l border-gray-200 sticky top-0"></div>
          {headers.map(header => {
            const isDifferent = isModified && fields_different.includes(header);
            return (
              <div 
                key={`header-${header}`} 
                className="p-2 text-xs font-semibold text-gray-500 bg-gray-50/50 border-b border-l border-gray-200 whitespace-nowrap sticky top-0 flex items-center justify-between gap-2"
              >
                <span className="capitalize">{header.replace(/_/g, ' ')}</span>
                {isDifferent && (
                  <button 
                    onClick={() => onOpenModal({
                      fieldName: header,
                      fromValue: from_db_row[header],
                      toValue: to_db_row[header]
                    })}
                    title="Side-by-side view"
                    className="p-1 text-gray-500 hover:bg-gray-300 hover:text-black rounded-full transition-colors"
                  >
                    <ArrowOutwardSharp style={{ fontSize: '1rem' }} />
                  </button>
                )}
              </div>
            );
          })}

            {/* "From" Data Row (Original) */}
            <div className="p-2 border-l border-gray-200 text-sm font-semibold text-gray-600 bg-red-50 flex items-center">
              From:
            </div>
          {headers.map(header => {
            const isDifferent = isModified && fields_different.includes(header);
            const cellClassName = `${isDifferent ? 'bg-red-100 font-medium' : ''} ${!from_db_row ? 'bg-gray-50' : ''}`;
            
            return (
              <div key={`from-${header}`} className={`p-2 border-l border-gray-200 text-sm whitespace-nowrap ${cellClassName}`}>
                <div className={`overflow-x-auto ${scrollbarClasses}`}>
                  {from_db_row ? 
                    formatCellContent(from_db_row[header]) : 
                    <span className="text-gray-400 italic">-- Row does not exist --</span>
                  }
                </div>
              </div>
            );
          })}

            {/* "To" Data Row (New/Modified) */}
            <div className="p-2 border-l border-gray-200 text-sm font-semibold text-gray-600 bg-green-50 flex items-center">
              To:
            </div>
          {headers.map(header => {
            const isDifferent = isModified && fields_different.includes(header);
            const cellClassName = `${isDifferent ? 'bg-green-100 font-medium' : ''} ${!to_db_row ? 'bg-gray-50' : ''}`;

            return (
              <div key={`to-${header}`} className={`p-2 border-l border-gray-200 text-sm whitespace-nowrap ${cellClassName}`}>
                 <div className={`overflow-x-auto ${scrollbarClasses}`}>
                  {to_db_row ? 
                    formatCellContent(to_db_row[header]) :  
                    <span className="text-gray-400 italic">-- Row will be added --</span>
                  }
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}